// ─── DataMap.jsx — Internal field-by-field data flow map ─────────────────────
// Hidden dev tool: shows every input field, what it affects, how it's calculated,
// and what overrides it. Access via URL param: #dashboard?datamap=true
// All descriptions use UI language — no variable names, no function names.
import { useState, useMemo } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";

// ─── FIELD DEFINITIONS ──────────────────────────────────────────────────────
export const FIELDS = [

  // ═══════════════════════════════════════════════════════════════════════════
  // MARKETPLACES PAGE
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Marketplaces", section: "Manual Fields", name: "% Total",
    what: "The percentage of your total business revenue that comes from this marketplace.",
    type: "Percentage",
    auto: "Yes — when you click the Recompute button, it recalculates from actual sales data in the selected time window (last 30, 60, or 90 days).",
    calc: `When you click "Recompute":
1. The app looks at each product's recent sales volume from the selected window (e.g., last 30 days)
2. It sums up all sales across all products in THIS marketplace to get the marketplace total
3. It sums up all sales across ALL marketplaces to get the brand total
4. This marketplace's % Total = this marketplace's sales divided by brand total sales

Example: Amazon sold 6,000 units in 30 days. Shopify sold 4,000. Brand total = 10,000.
Amazon % Total = 6,000 / 10,000 = 60%. Shopify = 40%.

When you type it manually, the value you enter is used directly.`,
    affects: ["Dashboard → Cashflow Chart (income line)", "Dashboard → Blended Payout/Unit", "Cash Calendar → Monthly Income breakdown", "Targets → Chart target bars (when marketplace selected)", "Targets → Stockout Date projection", "Every KPI that uses per-marketplace income"],
    overriddenBy: ["Recompute button — recalculates from real sales data", "API sync — captures fresh sales volumes, then Recompute applies them"],
    overrides: [],
    warn: "All marketplaces must add up to 100%. If they don't, income projections will be inaccurate." },

  { page: "Marketplaces", section: "Manual Fields", name: "Payout Delay",
    what: "How many days after a sale this marketplace takes to transfer money to your bank account.",
    type: "Number (days)",
    auto: "Can be set by API sync for some platforms.",
    calc: `This number shifts your income forward in time on the cashflow chart.

Example: Amazon pays every 14 days. If you sell 100 units on April 1st, the income from those sales appears on April 15th in your cashflow — not April 1st.

This means your cash balance dips for 14 days before the payout arrives. A higher delay = more cash pressure.`,
    affects: ["Dashboard → Cashflow Chart (income appears later)", "Cash Calendar → Income shows in the month it arrives, not when sold", "Dashboard → Cash-to-Cash Cycle calculation", "All daily balance projections"],
    overriddenBy: ["API sync (for platforms that report payout schedule)"],
    overrides: [],
    warn: "Amazon is typically 14 days, Shopify 2-3 days, eBay 2-3 days." },

  { page: "Marketplaces", section: "Manual Fields", name: "Reserve Held",
    what: "The dollar amount currently held in reserve by this marketplace — money they owe you but haven't released yet.",
    type: "Currency amount",
    auto: "Yes — Amazon and Shopify report this via API sync. For eBay/Walmart, use Reserve Rate instead.",
    calc: `Shown as a KPI on the Dashboard to inform you how much cash is locked up.

When set via API: the synced value is used directly.
When set manually: your entered value overrides the API value. A "Reset to API" button appears.
When neither is set but Reserve Rate is: the app estimates the held amount from your recent 30 days of payouts multiplied by the rate percentage.

This value is NOT deducted from your cashflow projections — reserves are temporary holds that will eventually be released to you.`,
    affects: ["Dashboard → Reserve Held KPI card", "Dashboard → tooltip shows per-marketplace breakdown"],
    overriddenBy: ["API sync (Amazon/Shopify report this automatically)"],
    overrides: ["Reserve Rate estimate — if you set a manual amount, it takes priority over the rate-based estimate"],
    warn: "" },

  { page: "Marketplaces", section: "Manual Fields", name: "Reserve Rate",
    what: "A percentage used to ESTIMATE how much this marketplace is holding in reserve, based on your recent payouts. Used for platforms that don't report reserves via API (eBay, Walmart).",
    type: "Percentage",
    auto: "No — you enter the rate from your marketplace's Seller Hub or Seller Center.",
    calc: `The app takes your last 30 days of API-synced income for this marketplace, multiplies it by this percentage, and shows the result as the estimated reserve amount.

Example: Your eBay payout over the last 30 days was $10,000. You set Reserve Rate to 5%. Estimated reserve = $10,000 × 5% = $500.

This estimate updates whenever your payout data changes (daily via the KPI snapshot).`,
    affects: ["Dashboard → Reserve Held KPI (shows estimated amount with a percentage badge)"],
    overriddenBy: ["Reserve Held (manual amount) — if you enter a dollar amount directly, it takes priority over this rate-based estimate"],
    overrides: [],
    warn: "Only useful for platforms without a reserve API. For Amazon/Shopify, use API sync instead." },

  { page: "Marketplaces", section: "Manual Fields", name: "Avg COGS/unit",
    what: "Average cost of goods sold per unit for this entire marketplace. Sets the cost used for inventory valuation when no per-product or per-group cost is entered.",
    type: "Currency amount (per unit)",
    auto: "No — always manual.",
    calc: `This is a fallback in the cost hierarchy. The app looks for cost in this order (effectiveCostPerUnitForPool in src/lib/calc/marketplaces.js):
1. Pool's own initialCostPerUnit (highest priority — overrides all)
2. Marketplace Avg COGS/unit ← you are here. When a pool spans multiple marketplaces, each marketplace's avgCogs is weighted by its pctOfTotal (re-normalized within the pool). Marketplaces in the pool that have no avgCogs set fall through to step 3 for THEIR slice.
3. Product Group COGS (per-product fallback if the product belongs to a group)
4. Per-product COGS (sum of Product Cost + Shipping + Customs + Prep)
5. Zero — user must enter manually.

The first non-zero value found at each step is used.

Example: You set Avg COGS to $8/unit. All 50 products in this marketplace are now valued at $8/unit for Money in Inventory, regardless of their individual COGS entries — UNLESS the pool has its own initialCostPerUnit set, which overrides this entirely.`,
    affects: ["Dashboard → Money in Inventory KPI (units × cost per unit)", "Targets → Inventory value display", "Targets → Stockout projections (when cost affects reorder calculations)"],
    overriddenBy: ["Inventory Pool cost per unit — if the pool has a cost set, it wins"],
    overrides: ["Product Group COGS", "Per-product COGS (all 4 components)"],
    warn: "Setting this to 0 falls back to group/product-level costs." },

  { page: "Marketplaces", section: "Manual Fields", name: "Ad Cost/unit",
    what: "Marketplace-level advertising cost per unit — what you spend on ads (PPC, sponsored products) for each unit sold on this marketplace.",
    type: "Percentage of payout OR fixed dollar amount (toggle between the two)",
    auto: "No — marketplace APIs don't include ad spend data.",
    calc: `This is a fallback in the ad cost hierarchy. The app looks for ad cost in this order:
1. Per-product ad cost (if set on the product card)
2. Product Group ad cost (if the product's group has ad cost set)
3. THIS marketplace's Ad Cost/unit ← you are here

When set as percentage: the app takes each product's payout and multiplies by this rate.
Example: Payout is $12/unit, ad rate is 15% → ad cost = $1.80/unit.

When set as fixed amount: that exact dollar amount is used per unit.

If "Deduct from projection" is enabled on a product, this ad cost is subtracted from the payout before calculating income.
If "Generate ad spend as expenses" is enabled, ad costs appear as expense line items on Cash Calendar.`,
    affects: ["Dashboard → Blended Payout/Unit (if deducted)", "Cash Calendar → Ad spend expense line items (if expense generation is on)", "Profitability → Product profit margin calculations"],
    overriddenBy: ["Per-product ad cost — if set on the product, it overrides this", "Product Group ad cost — if set on the group, it overrides this for group members"],
    overrides: [],
    warn: "" },

  { page: "Marketplaces", section: "Manual Fields", name: "Stock Override",
    what: "Manually lock this marketplace's stock to a specific number of units. API syncs will not overwrite this value.",
    type: "Number (units)",
    auto: "No — this is a sticky manual override.",
    calc: `When set, this number becomes the marketplace's stock value in the resolution hierarchy:
1. Inventory Pool stock override (highest priority)
2. THIS marketplace's stock override ← you are here
3. Per-product stock (sum of each product's manual or API stock)
4. API-synced marketplace total
5. Zero

Setting this locks the value — API syncs capture their new number in the background but don't replace yours. A "Reset to API" button appears so you can revert.

Example: API says 5,000 units but you know 500 are damaged. Set override to 4,500.`,
    affects: ["Dashboard → Money in Inventory KPI", "Targets → Stock projection chart", "Targets → Stockout Date", "Targets → Reorder Date"],
    overriddenBy: ["Inventory Pool stock override — if the pool has a manual override, it wins"],
    overrides: ["Per-product stock values", "API-synced stock total"],
    warn: "Set to 0 to clear the override and fall back to API/per-product values." },

  // ── Products ──
  { page: "Marketplaces", section: "Product Card", name: "Payout/Unit",
    what: "How much money you receive per unit sold for this product, after marketplace fees.",
    type: "Currency amount",
    auto: "Can be set by API sync (calculated from recent settlement data).",
    calc: `This is the per-unit income for this product. The app uses it to calculate:

Monthly income from this product = target units × this marketplace's % Total × this product's % of Market × Payout/Unit

Example: Brand target is 10,000 units. Amazon is 60% of sales. This product is 30% of Amazon.
Units for this product = 10,000 × 60% × 30% = 1,800.
Income = 1,800 × $12/unit = $21,600.

If ad cost or refund deductions are enabled, they reduce this payout before the income calculation.`,
    affects: ["Dashboard → Cashflow Chart (income)", "Dashboard → Blended Payout/Unit", "Cash Calendar → Monthly income breakdown", "Targets → Revenue display on month cards"],
    overriddenBy: ["Monthly override — you can set a different payout for a specific month via month mode"],
    overrides: [],
    warn: "" },

  { page: "Marketplaces", section: "Product Card", name: "% of Market",
    what: "What percentage of THIS marketplace's sales comes from this product.",
    type: "Percentage",
    auto: "Yes — recalculated by the Recompute button from actual sales data.",
    calc: `Works the same way as marketplace % Total, but within one marketplace.

When you click Recompute:
1. Each product's recent sales volume is read from the selected time window
2. The product's share = its sales divided by the marketplace's total sales

All products in a marketplace should add up to 100%.

Example: Product A sold 700 units, Product B sold 300 units. Total = 1,000.
Product A = 70%, Product B = 30%.`,
    affects: ["Dashboard → Cashflow Chart (income per product)", "Cash Calendar → Income breakdown by product", "Targets → per-product stock projections"],
    overriddenBy: ["Recompute button", "Monthly override (month mode)"],
    overrides: [],
    warn: "All products in a marketplace should add up to 100%." },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "COGS Breakdown",
    what: "The landed cost to get one unit of this product to your warehouse. Split into 4 components: Product Cost, Shipping, Customs, Prep.",
    type: "4 currency inputs that are summed",
    auto: "No — always manual.",
    calc: `The total COGS per unit = Product Cost + Shipping + Customs + Prep.

This is the lowest priority in the cost hierarchy:
1. Inventory Pool cost per unit
2. Marketplace Avg COGS/unit
3. Product Group COGS
4. THIS product's COGS breakdown ← you are here

Used for: Money in Inventory = stock units × cost per unit.

Example: Product cost $5.00 + Shipping $1.50 + Customs $0.80 + Prep $0.20 = $7.50/unit.
If you have 1,000 units in stock, Money in Inventory = $7,500.`,
    affects: ["Dashboard → Money in Inventory KPI", "Targets → Inventory value"],
    overriddenBy: ["Marketplace Avg COGS/unit", "Product Group COGS", "Inventory Pool cost per unit"],
    overrides: [],
    warn: "" },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Delivery/Unit",
    what: "Fulfillment or delivery cost per unit (warehouse, 3PL, FBM courier).",
    type: "Currency amount + 'Deduct from payout' checkbox",
    auto: "No — always manual.",
    calc: `When the 'Deduct from payout' checkbox is ON: the value is subtracted from this product's Payout/Unit at calculation time. Lowers projected income on the Dashboard chart.

When OFF: the value is purely informational — no cash effect.

NEVER appears as a separate Cash Calendar expense row.`,
    affects: ["Dashboard → Cashflow Chart (income line) — only when 'Deduct from payout' is ON", "Blended Payout/Unit KPI", "Targets income projection", "Money in Inventory (cost basis unaffected)"],
    overriddenBy: [],
    overrides: [],
    warn: "FBA / API-connected marketplaces (Amazon, Shopify, Walmart, eBay) already deduct shipping and fulfillment fees from your payout — leaving 'Deduct from payout' ON would double-count. Only enable it for FBM (self-fulfilled) products or for manual marketplaces that don't sync via API." },

  // ── Inventory Pools ──
  { page: "Marketplaces", section: "Inventory Pools", name: "Pool Stock (units)",
    what: "The number of units currently in this inventory pool. This is the anchor for all stock projections.",
    type: "Number (units)",
    auto: "Yes — API sync updates this when a marketplace in the pool is synced (unless you have a manual override set).",
    calc: `This is the starting point for the stock depletion projection.

The app walks forward day by day from today:
- Each day, it subtracts the daily sales rate (monthly target divided by days in the month)
- When an order batch arrives, it adds those units
- When stock hits zero, that's the Stockout Date

The stock hierarchy (highest priority wins):
1. Shared pool's own stock (if pool is marked as "shared") ← you are here
2. Manual pool override (if you edited the stock and it's locked)
3. Sum of per-marketplace stocks for marketplaces in this pool
4. Pool's baseline stock
5. Zero

A "Reset to API" button appears when you manually edit this after an API sync.`,
    affects: ["Dashboard → Money in Inventory KPI", "Targets → Stock projection line on chart", "Targets → Stockout Date KPI", "Targets → Reorder Date KPI", "Targets → Insufficient line on chart"],
    overriddenBy: ["Nothing — pool stock is the highest priority in the hierarchy"],
    overrides: ["Per-marketplace stock overrides", "Per-product stock", "API-synced stock"],
    warn: "A marketplace can only belong to ONE pool." },

  { page: "Marketplaces", section: "Inventory Pools", name: "Cost Per Unit (pool)",
    what: "The average cost per unit for inventory in this pool. Used to calculate Money in Inventory.",
    type: "Currency amount",
    auto: "No — manual entry.",
    calc: `Money in Inventory = Pool Stock × Cost Per Unit.

This is the highest priority in the cost hierarchy:
1. THIS pool's cost per unit ← you are here
2. Marketplace Avg COGS/unit
3. Product Group COGS
4. Per-product COGS breakdown

Example: Pool has 5,000 units at $8/unit. Money in Inventory = $40,000.`,
    affects: ["Dashboard → Money in Inventory KPI", "Targets → Inventory value display"],
    overriddenBy: ["Nothing — pool cost is the highest priority"],
    overrides: ["Marketplace Avg COGS/unit", "Product Group COGS", "Per-product COGS"],
    warn: "Set to 0 to fall back to the marketplace/group/product cost hierarchy." },

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Dashboard", section: "KPI Cards", name: "Available Today",
    what: "Your current cash balance across all bank accounts. This is the starting point for all cashflow projections.",
    type: "Currency amount",
    auto: "No — you enter your real bank balance manually.",
    calc: `Every cashflow projection starts from this number.

The app projects forward day by day:
Starting balance = Available Today
Each day: balance = previous balance + income received that day - expenses due that day

Income comes from: marketplace payouts (target × payout × marketplace share, delayed by payout delay days)
Expenses come from: supplier order payments, recurring transactions, delivery costs, ad spend, modifiers

The resulting line on the Dashboard chart shows where your cash is heading.`,
    affects: ["Dashboard → Cashflow Chart (starting point)", "Dashboard → Runway KPI (days until balance goes negative)", "Dashboard → Year-End Balance", "Dashboard → Health Score", "Cash Calendar → Running balance", "All affordability calculations"],
    overriddenBy: ["Nothing — you are the source of truth for your bank balance"],
    overrides: [],
    warn: "Update this regularly (weekly or after large transactions) for accurate projections." },

  // ═══════════════════════════════════════════════════════════════════════════
  // TARGETS
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Targets", section: "Month Cards", name: "Monthly Target (units)",
    what: "How many units you plan to sell this month across all marketplaces.",
    type: "Number (units)",
    auto: "No — you set this manually. You can copy from a previous year with a growth percentage.",
    calc: `This is the central planning number. It drives:

Income projection:
Each marketplace gets its share: target × marketplace % Total.
Each product gets its share: marketplace units × product % of Market.
Income = product units × payout per unit (shifted by payout delay days).

Stock depletion:
Daily sales rate = monthly target / days in the month.
Stock decreases by this rate each day on the Targets chart.

Delivery expenses:
Monthly delivery cost = target units × marketplace share × product share × delivery cost per unit.

Example: Target = 10,000 units. Amazon (60%) sells 6,000. At $12 payout/unit with 14-day delay, income of $72,000 arrives ~2 weeks after sales.`,
    affects: ["Dashboard → Cashflow Chart (income + delivery expenses)", "Dashboard → Current Month KPI", "Cash Calendar → Income and delivery expense items", "Targets → Chart bars", "Targets → Stock depletion rate", "Targets → Stockout Date", "Targets → Reorder Date"],
    overriddenBy: ["Per-marketplace target override — when you select one marketplace and change its target, that override is used instead of the share-based calculation"],
    overrides: [],
    warn: "" },

  { page: "Targets", section: "Month Cards", name: "Actual Sold",
    what: "How many units were actually sold this month. Used to compare against your target and for revenue accuracy.",
    type: "Number (units)",
    auto: "Can be synced from marketplace APIs.",
    calc: `For past months: actual sold replaces the target in revenue calculations, giving you real numbers instead of projections.

For the current month: the app calculates a run rate.
Run rate = actual sold so far / days elapsed this month.
Projected remaining = run rate × days left in the month.
Projected total = actual + projected remaining.

This is used to determine if you're "On Track", "Behind", or "Off Track" on the Targets page.`,
    affects: ["Targets → On Track / Behind / Off Track status", "Targets → Hit Rate percentage", "Dashboard → Current month income (uses actual instead of target for past months)", "Targets → Stock reconstruction for past months"],
    overriddenBy: ["API sync — marketplace APIs report actual order counts"],
    overrides: ["Monthly target — for past months, actual sold is used instead of the target for revenue calculations"],
    warn: "" },

  { page: "Targets", section: "Controls", name: "Shrinkage %",
    what: "Percentage of incoming stock that is expected to be lost, damaged, or unusable. Applied to all order batch arrivals.",
    type: "Percentage (default 2%)",
    auto: "No — you set this based on your experience.",
    calc: `When an order batch arrives, the actual stock added is reduced by this percentage.

Effective units received = batch units × (1 - shrinkage / 100)

Example: Order of 1,000 units arrives. Shrinkage is 3%.
Effective stock added = 1,000 × 0.97 = 970 units.
30 units are assumed lost to damage/returns/defects.

This affects all stock projections going forward — stockout date, insufficient line, reorder date.`,
    affects: ["Targets → Stock projection line on chart", "Targets → Stockout Date", "Targets → Reorder Date", "Dashboard → Money in Inventory (fewer units = lower value)"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "Set to 0% to receive the full order quantity. Typical range: 1-5%." },

  { page: "Targets", section: "Controls", name: "Custom Lead Time",
    what: "Override the automatically calculated lead time (days from placing an order to receiving stock). Used when your order history doesn't reflect your actual lead time.",
    type: "Number (days)",
    auto: "When not set, the app uses the lead time from your most recent order (calculated from order date to arrival date).",
    calc: `Lead time is used to calculate the Reorder Date:
Reorder Date = Stockout Date - Lead Time

Example: Stockout projected for June 3rd. Lead time is 60 days.
Reorder Date = June 3 - 60 days = April 4th.
If today is after April 4th, the reorder line shows as "Overdue".

When you select a specific marketplace, the lead time is calculated only from orders linked to that marketplace — not a brand-wide average.`,
    affects: ["Targets → Reorder Date KPI", "Targets → Reorder line on chart (orange thick line)"],
    overriddenBy: ["Nothing — this overrides the auto-calculated lead time"],
    overrides: ["Auto-calculated lead time from order history"],
    warn: "Set to 0 to use the automatically calculated lead time from your orders." },

  // ═══════════════════════════════════════════════════════════════════════════
  // SUPPLIER ORDERS
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Supplier Orders", section: "Order Card", name: "Order Amount",
    what: "The total cost of this supplier order in your currency.",
    type: "Currency amount",
    auto: "No — always manual.",
    calc: `This amount is split across payment terms based on the percentage you assign to each term.

Example: Order total is $50,000. Payment terms: 30% on order ($15,000), 70% on arrival ($35,000).
Each payment generates an expense entry on your Cash Calendar on its due date.

These expenses are subtracted from your cash balance on the dashboard chart.`,
    affects: ["Dashboard → Cashflow Chart (expense line for each payment)", "Cash Calendar → Payment due items", "Marketplaces → Cash Projected in Orders KPI", "Dashboard → Next Payment Due"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "Orders without an amount set will show a warning — the blocked-in-transit calculation will be incomplete." },

  { page: "Supplier Orders", section: "Container Batch", name: "Arrival Date",
    what: "When this batch of containers is expected to arrive at your warehouse or fulfillment center.",
    type: "Date (DD.MM.YYYY)",
    auto: "No — you enter the expected date and update it as tracking information comes in.",
    calc: `This date determines when the batch's units are added to your stock projection.

On this date in the Targets chart:
- Stock jumps up by the batch's unit count (minus shrinkage)
- A diamond marker appears on the chart

This date also triggers payment terms that use "days after arrival" as their trigger:
Payment due date = Arrival Date + days specified in the payment term.

Example: Arrival date April 15. Payment term "50% due 30 days after arrival".
Payment due = May 15 for $25,000.`,
    affects: ["Targets → Stock projection (units added on this date)", "Targets → Diamond markers on chart", "Cash Calendar → Payment due dates (for arrival-triggered terms)", "Dashboard → Cashflow Chart (payment expenses)"],
    overriddenBy: ["In-Stock Date — if you set an explicit in-stock date, it overrides arrival + buffer days"],
    overrides: [],
    warn: "" },

  { page: "Supplier Orders", section: "Container Batch", name: "Unit Count (batch)",
    what: "How many units are in this specific batch/shipment.",
    type: "Number (units)",
    auto: "If not set, the app distributes the order's total unit count proportionally across batches based on container count.",
    calc: `These units are added to your stock on the batch's arrival date (minus shrinkage).

If not set per-batch, the app calculates:
Batch units = (batch containers / total order containers) × order total units

Example: Order has 5,000 units total, 2 batches.
Batch 1 has 3 containers, Batch 2 has 2 containers. Total = 5 containers.
Batch 1 gets 3/5 × 5,000 = 3,000 units. Batch 2 gets 2,000 units.`,
    affects: ["Targets → Stock projection (units added on arrival date)", "Dashboard → Money in Inventory (after arrival)", "Targets → Stockout Date"],
    overriddenBy: ["Nothing — if set, it overrides the proportional calculation"],
    overrides: ["Proportional unit allocation based on container count"],
    warn: "" },

  { page: "Supplier Orders", section: "Payment Terms", name: "Payment Term %",
    what: "What percentage of the total order amount is due for this payment milestone.",
    type: "Percentage (0-100%)",
    auto: "No — you define the split.",
    calc: `Payment amount = order total × this percentage / 100.

Example: Order is $100,000. Term 1 is 30% = $30,000 due on order date. Term 2 is 70% = $70,000 due on arrival.

Each payment becomes an expense on your Cash Calendar on its due date. All terms should add up to 100%.`,
    affects: ["Dashboard → Cashflow Chart (expense on due date)", "Cash Calendar → Payment items", "Dashboard → Next Payment Due KPI"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "All payment terms should add up to 100%. A warning appears if they don't." },

  // ═══════════════════════════════════════════════════════════════════════════
  // RECURRING TRANSACTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Recurring", section: "Transaction Card", name: "Amount",
    what: "The recurring amount for this transaction — what you pay or receive each time it occurs.",
    type: "Currency amount",
    auto: "No — you enter the base amount. It can grow over time if you set a growth rate.",
    calc: `This amount appears on your Cash Calendar for every month/period the transaction is active.

With growth rate: the amount increases by the growth percentage at the specified frequency.
Example: Salary starts at $3,000/month with 5% growth every 12 months.
Year 1: $3,000/month. Year 2: $3,150/month. Year 3: $3,307.50/month.

Income items are added to your cash balance. Expense items are subtracted.`,
    affects: ["Dashboard → Cashflow Chart (income or expense line)", "Cash Calendar → Monthly transaction items", "Dashboard → Projected Income or Projected Expenses"],
    overriddenBy: ["Cash Calendar partial payment — you can mark a different amount as paid for a specific month"],
    overrides: [],
    warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Growth Rate %",
    what: "How much this transaction's amount increases over time.",
    type: "Percentage",
    auto: "No — you enter the growth rate.",
    calc: `The amount grows by this percentage at the frequency you specify.

Growth frequency options:
- Every 1 transaction: amount grows each time it occurs
- Every 12 transactions: amount grows once a year (annual raise)

New amount = previous amount × (1 + growth rate / 100)

Example: Rent is $2,000/month with 3% growth every 12 months.
Months 1-12: $2,000. Months 13-24: $2,060. Months 25-36: $2,121.80.`,
    affects: ["Dashboard → Cashflow Chart (future months show higher amounts)", "Cash Calendar → Growing amounts over time"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "" },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODIFIERS
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Modifiers", section: "Modifier Card", name: "Modifier Value (%)",
    what: "The percentage adjustment this modifier applies. Positive values increase, negative values decrease.",
    type: "Percentage (can be negative)",
    auto: "No — you enter the adjustment.",
    calc: `Depends on the modifier type:

Payout modifier: Changes income per unit.
Adjusted income = normal income × (1 + value / 100)
Example: +15% payout modifier in November → Black Friday premium pricing.

Units modifier: Changes how many units are sold.
Adjusted units = normal target × (1 + value / 100)
Example: -40% units modifier March 15-30 → stockout period, fewer sales.

Fulfilment modifier: Changes delivery/fulfillment costs.
Example: +12% in Q4 → FBA fee surcharge.

Partner modifier: Changes partner/company costs.
Example: +8% from July → rent increase.

Each modifier only applies to the months you select.`,
    affects: ["Dashboard → Cashflow Chart (adjusted income or expenses)", "Cash Calendar → Modified amounts", "Targets → Chart shows modified target bars", "Targets → Stockout projection uses modified targets"],
    overriddenBy: ["Nothing"],
    overrides: ["Base income, targets, or costs for the selected months"],
    warn: "Active modifiers show a warning badge on Targets month cards." },

  // ═══════════════════════════════════════════════════════════════════════════
  // SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Settings", section: "Display & Format", name: "Currency",
    what: "Which currency symbol to show throughout the app.",
    type: "Toggle (USD / EUR / GBP)",
    auto: "No — you select your currency.",
    calc: `This only changes the display symbol ($ / € / £). It does NOT convert any values.
All your numbers should be entered in one consistent currency.`,
    affects: ["Every currency display throughout the app"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "Changing currency does not convert values — it only changes the symbol." },

  { page: "Settings", section: "Cashflow Horizon", name: "Visible Years",
    what: "Which years appear across the app — Targets, Cash Calendar, Cashflow Chart.",
    type: "Checkboxes (one per year)",
    auto: "No — you select which years to show. Current year is always visible.",
    calc: `Adding a future year extends your forecast horizon. Removing a past year hides its data from view (data is not deleted).`,
    affects: ["Targets → Which year sections appear", "Cash Calendar → Which months are available", "Dashboard → Chart time range", "All time-series displays"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "The current year cannot be deselected." },

  // ═══════════════════════════════════════════════════════════════════════════
  // MARKETPLACES — remaining product fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Ad Cost (per product)",
    what: "Advertising cost for this specific product — what you spend on PPC or sponsored ads per unit sold.",
    type: "Percentage of payout OR fixed dollar amount",
    auto: "No — marketplace APIs don't report ad spend.",
    calc: `This is the highest priority in the ad cost hierarchy:
1. THIS product's ad cost ← you are here
2. Product Group ad cost
3. Marketplace Ad Cost/unit

If set as percentage: ad cost = product payout × this rate.
If set as fixed amount: that exact dollar amount per unit.

Example: Product payout is $15/unit. Ad cost rate is 20%. Ad cost = $3/unit.
Net payout after deduction = $12/unit.`,
    affects: ["Dashboard → Blended Payout/Unit (if deduction enabled)", "Cash Calendar → Ad spend expense items (if expense generation enabled)", "Profitability → Product margin"],
    overriddenBy: ["Nothing — product-level is highest priority"],
    overrides: ["Product Group ad cost", "Marketplace Ad Cost/unit"],
    warn: "" },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Refund Rate / Amount",
    what: "Expected refund or return rate for this product. Can be a percentage of payout or a fixed amount per unit.",
    type: "Percentage OR fixed dollar amount",
    auto: "No.",
    calc: `When "Deduct from projection" is enabled:
Percentage mode: effective payout = payout × (1 - refund rate)
Fixed mode: effective payout = payout - refund amount

Example: Payout $15, refund rate 5%. Effective payout = $15 × 0.95 = $14.25/unit.
This reduced payout is used in all income projections.`,
    affects: ["Dashboard → Cashflow Chart (reduced income)", "Cash Calendar → Lower projected income", "Profitability → Product margin"],
    overriddenBy: ["Monthly override (month mode)"],
    overrides: [],
    warn: "Only applies when 'Deduct from projection' is enabled on this product." },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Product Stock (per product)",
    what: "Manual stock count for this specific product.",
    type: "Number (units)",
    auto: "Can be set by API sync.",
    calc: `This is a low-priority value in the stock hierarchy:
1. Pool stock override (highest)
2. Marketplace stock override
3. THIS product's stock ← you are here (summed across all products in the marketplace)
4. API-synced marketplace total
5. Zero

When you set this, it's summed with other products' stock values to form the marketplace's contribution to the pool.`,
    affects: ["Dashboard → Money in Inventory (units × cost)", "Targets → Stock projection", "Targets → Stockout Date"],
    overriddenBy: ["Marketplace stock override", "Pool stock override"],
    overrides: ["API-synced stock for this product"],
    warn: "" },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Generate Ad Spend as Expenses",
    what: "Toggle that creates ad spend expense entries on your Cash Calendar based on this product's ad cost.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: `When enabled, the app generates expense line items on Cash Calendar:
Monthly ad expense = units sold × ad cost per unit.

The schedule can be monthly (on a specific day), weekly, or every X days — set via the schedule options below this toggle.

Example: 1,000 units/month × $2 ad cost = $2,000/month expense appearing on your Cash Calendar.`,
    affects: ["Cash Calendar → Ad spend expense items", "Dashboard → Cashflow Chart (expense line)"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "This creates ADDITIONAL expense entries. The 'Deduct from projection' toggle separately reduces income." },

  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Deduct Ad Cost from Projection",
    what: "Toggle that subtracts the ad cost from this product's payout before calculating income projections.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: `When enabled, income projections use the net payout (after ad cost deduction).

Without deduction: income = units × $15/unit = $15,000
With deduction (20% ad cost): income = units × ($15 - $3) = $12,000

This is a display/projection adjustment — the actual ad spend as a Cash Calendar expense is controlled separately by 'Generate Ad Spend as Expenses'.`,
    affects: ["Dashboard → Cashflow Chart (lower income)", "Dashboard → Blended Payout/Unit (lower)", "Cash Calendar → Lower projected marketplace income"],
    overriddenBy: ["Nothing"],
    overrides: [],
    warn: "" },

  { page: "Marketplaces", section: "Carousel Card", name: "Marketplace Name",
    what: "The display name for this sales channel.",
    type: "Text",
    auto: "Set to the platform name on creation (e.g., 'Amazon'). Editable anytime.",
    calc: "No calculation — used for display and identification only.",
    affects: ["All pages — marketplace labels, dropdowns, tooltips"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Marketplaces", section: "Toolbar", name: "Share Window",
    what: "The lookback period for the Recompute button — how many days of recent sales data to use when recalculating marketplace and product shares.",
    type: "Dropdown (Last 7 / 14 / 30 / 60 / 90 days)",
    auto: "No.",
    calc: `When you click Recompute, the app uses sales data from this many recent days to calculate each product's and marketplace's percentage share.

Shorter window (7 days): more responsive to recent trends but noisier.
Longer window (90 days): more stable average but slower to reflect changes.`,
    affects: ["Marketplace % Total (via Recompute)", "Product % of Market (via Recompute)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Marketplaces", section: "Product Group", name: "Group COGS",
    what: "Cost of goods sold per unit for all products in this group. Overrides per-product COGS but is overridden by marketplace and pool-level costs.",
    type: "Currency amount (per unit)",
    auto: "No.",
    calc: `Position in the cost hierarchy:
1. Pool cost per unit (highest)
2. Marketplace Avg COGS/unit
3. THIS group's COGS ← you are here
4. Per-product COGS breakdown (Product Cost + Shipping + Customs + Prep)

Example: Group has 5 products. Setting Group COGS to $7/unit means all 5 products are valued at $7/unit for inventory calculations, regardless of their individual COGS entries.`,
    affects: ["Dashboard → Money in Inventory KPI", "Targets → Inventory value"],
    overriddenBy: ["Marketplace Avg COGS/unit", "Pool cost per unit"],
    overrides: ["Per-product COGS breakdown for all products in this group"],
    warn: "Set to 0 to fall back to per-product COGS." },

  { page: "Marketplaces", section: "Product Group", name: "Group Ad Cost",
    what: "Advertising cost per unit for all products in this group. Overrides per-product ad cost.",
    type: "Percentage of payout OR fixed dollar amount",
    auto: "No.",
    calc: `Position in the ad cost hierarchy:
1. Per-product ad cost (highest)
2. THIS group's ad cost ← you are here
3. Marketplace Ad Cost/unit

Example: Group has 3 products. Setting Group Ad Cost to 12% means all 3 products use 12% of their payout as ad cost, unless they have their own per-product override.`,
    affects: ["Same as per-product ad cost — income projections, Cash Calendar expenses, Profitability"],
    overriddenBy: ["Per-product ad cost"],
    overrides: ["Marketplace Ad Cost/unit for products in this group"],
    warn: "" },

  { page: "Marketplaces", section: "Inventory Pools", name: "Shared Pool Toggle",
    what: "Whether this pool represents physically shared inventory (one warehouse serving multiple sales channels).",
    type: "Toggle (Shared / Aggregate)",
    auto: "No — set during pool creation when adding a second marketplace.",
    calc: `Shared mode: The pool's stock number is the absolute truth. It represents one physical pile of inventory. Per-marketplace stock values are ignored.

Aggregate mode: Each marketplace reports its own stock independently. The pool total is the sum of all marketplace stocks. Used when marketplaces have separate warehouses.

Example: Amazon FBA + Shopify both sell from the same warehouse → Shared.
Amazon FBA + eBay with separate warehouses → Aggregate.`,
    affects: ["How pool stock is resolved — shared uses pool value directly, aggregate sums per-marketplace values", "Dashboard → Money in Inventory", "Targets → Stock projection"],
    overriddenBy: [], overrides: [], warn: "A marketplace can only belong to ONE pool." },

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Dashboard", section: "What-If Simulator", name: "Sim Order Amount",
    what: "The total cost of a hypothetical supplier order you're considering placing.",
    type: "Currency amount",
    auto: "No — you enter a hypothetical amount to see its impact.",
    calc: `The simulator takes this amount and splits it across the payment terms you define. Each payment is overlaid on your cashflow chart as a dashed line, showing how the order would affect your cash balance.

It does NOT save any data — it's purely a visual what-if analysis.`,
    affects: ["Dashboard → Cashflow Chart (dashed overlay line)", "Dashboard → Sim panel KPIs (runway, lowest point, affordability)"],
    overriddenBy: [], overrides: [],
    warn: "This is a simulation only — click 'Create Order' to save it as a real order." },

  { page: "Dashboard", section: "What-If Simulator", name: "Sim Order Units",
    what: "How many units the hypothetical order would contain.",
    type: "Number (units)",
    auto: "No.",
    calc: `Used to calculate:
- Stock after arrival = current stock + these units
- Sell-through time = units / daily sales rate
- Inventory value change = units × cost per unit`,
    affects: ["Dashboard → Sim panel 'Stock after' display", "Dashboard → Sim panel 'Sell-through' days"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Dashboard", section: "What-If Simulator", name: "Sim Payment Terms",
    what: "How the hypothetical order's payment would be structured (deposit, balance on arrival, etc.).",
    type: "Multiple rows: label + percentage + trigger + date",
    auto: "No.",
    calc: `Same logic as real order payment terms.
Each term generates a hypothetical expense on the cashflow chart.

Example: 30% deposit on order date ($15,000), 70% on arrival ($35,000).
The chart shows these as dashed expense markers with balance-before and balance-after.`,
    affects: ["Dashboard → Cashflow Chart (dashed expense markers)", "Dashboard → Sim panel affordability check"],
    overriddenBy: [], overrides: [], warn: "Terms should add up to 100%." },

  { page: "Dashboard", section: "What-If Simulator", name: "Sim Arrival Date",
    what: "When the hypothetical order would arrive at your warehouse.",
    type: "Date",
    auto: "No.",
    calc: `Used to calculate:
- Days until arrival
- When stock would increase
- Cash-to-cash cycle (deposit date → sell-through end)`,
    affects: ["Dashboard → Sim panel timeline", "Dashboard → Cashflow Chart (arrival marker)"],
    overriddenBy: [], overrides: [], warn: "" },

  // ═══════════════════════════════════════════════════════════════════════════
  // TARGETS — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Targets", section: "Controls", name: "Stockout Buffer (days)",
    what: "Extra safety buffer added before the stockout date. Shifts the 'danger zone' earlier so you get warned sooner.",
    type: "Number (days)",
    auto: "No.",
    calc: `The stockout buffer subtracts days from the projected stockout date for the reorder calculation.

Reorder deadline = Stockout Date - Lead Time - Buffer Days

Example: Stockout June 3. Lead time 60 days. Buffer 7 days.
Reorder by = June 3 - 60 - 7 = March 27.`,
    affects: ["Targets → Reorder KPI (earlier deadline)", "Targets → Reorder line on chart"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Targets", section: "Controls", name: "Danger % Threshold",
    what: "When current stock falls below this percentage of the monthly target, the month card turns red/orange as a visual warning.",
    type: "Percentage",
    auto: "No.",
    calc: `The app compares stock entering each month against the target:
- Stock ≥ target → green (safe)
- Stock ≥ target × danger% → orange (warning)
- Stock < target × danger% → red (critical)

Example: Target 3,000 units. Danger threshold 30%.
Stock 2,000 → orange (2,000 > 900 threshold but < 3,000 target).
Stock 800 → red (800 < 900 threshold).`,
    affects: ["Targets → Month card border color (red/orange/green)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Targets", section: "Controls", name: "Copy Growth %",
    what: "Growth percentage applied when copying targets from a previous year.",
    type: "Percentage (can be negative)",
    auto: "No.",
    calc: `When you click 'Copy [previous year]' or 'From actuals':
Each month's target = source value × (1 + growth% / 100)

Example: 2025 January was 5,000 units. Growth 20%.
2026 January target = 5,000 × 1.20 = 6,000 units.

Negative growth works too: -10% growth → 5,000 × 0.90 = 4,500.`,
    affects: ["Targets → All 12 month targets for the destination year"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Targets", section: "Month Cards", name: "Per-Marketplace Target Override",
    what: "When viewing a single marketplace, you can set a custom target for that marketplace that differs from its calculated share.",
    type: "Number (units)",
    auto: "Default is calculated: brand target × marketplace % Total.",
    calc: `Without override: marketplace target = brand target × % Total.
Example: Brand target 10,000, Amazon is 60% → Amazon target = 6,000.

With override: you type 8,000 directly for Amazon.
The extra 2,000 units (8,000 - 6,000 = +2,000 delta) rolls up to the brand total.
Brand total becomes 10,000 + 2,000 = 12,000.

This override persists per-month and per-marketplace. A reset button appears.`,
    affects: ["Targets → Chart bar for this marketplace", "Targets → KPI cards (year total, current month)", "Targets → Stock depletion rate (uses override instead of calculated share)"],
    overriddenBy: ["Nothing — once set, it persists until reset"],
    overrides: ["Calculated share (brand target × % Total)"],
    warn: "The delta rolls up to the brand total when viewing 'All Marketplaces'." },

  { page: "Targets", section: "Month Cards", name: "Month Note",
    what: "A free-text note attached to a specific month for your own reference.",
    type: "Text",
    auto: "No.",
    calc: "No calculation — for documentation only. Appears as a small note icon on the month card.",
    affects: [], overriddenBy: [], overrides: [], warn: "" },

  { page: "Targets", section: "Simulation Panel", name: "Sim % (Targets)",
    what: "A what-if percentage adjustment to your unit targets. Shows the impact on stock, stockout, and reorder dates.",
    type: "Percentage (can be negative) — applies to all months or per-month",
    auto: "No.",
    calc: `Adjusted target = base target × (1 + sim% / 100)

Two modes:
- All months: one percentage applied everywhere
- Per-month: custom percentage for each month (e.g., +20% December, -10% January)

The chart shows the simulated targets as adjusted bars. Stock projection uses the adjusted targets for depletion.

Example: Base target 5,000. Sim +20%. Adjusted = 6,000. Stock depletes faster.`,
    affects: ["Targets → Chart target bars (visually adjusted)", "Targets → Stock projection (faster/slower depletion)", "Targets → Sim panel KPIs (stockout, reorder, inventory value)"],
    overriddenBy: [], overrides: [],
    warn: "Simulation does NOT save — it's a visual what-if only." },

  // ═══════════════════════════════════════════════════════════════════════════
  // SUPPLIER ORDERS — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Supplier Orders", section: "Order Card", name: "Order Number",
    what: "A reference number for this order (e.g., PO-001).",
    type: "Text",
    auto: "Auto-generated as ORD-001, ORD-002, etc. Editable.",
    calc: "No calculation — identification and display only.",
    affects: [], overriddenBy: [], overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Order Card", name: "Order Date",
    what: "When this order was placed with the supplier.",
    type: "Date (DD.MM.YYYY)",
    auto: "Defaults to today when creating a new order.",
    calc: `Used as the baseline for payment terms with 'days after order' trigger.

Example: Order date April 1. Payment term '50% due 30 days after order'.
Payment due = May 1.

Also used to calculate lead time: Lead Time = Arrival Date - Order Date.`,
    affects: ["Cash Calendar → Payment due dates (for order-triggered terms)", "Targets → Lead time calculation", "Dashboard → Cashflow Chart"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Order Card", name: "Supplier Name",
    what: "The name of the supplier or factory for this order.",
    type: "Text",
    auto: "No.",
    calc: "No calculation — for identification and record-keeping only.",
    affects: [], overriddenBy: [], overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Container Batch", name: "Departure Date",
    what: "When this batch leaves the supplier's port or factory.",
    type: "Date (DD.MM.YYYY)",
    auto: "No.",
    calc: `Used for:
- Kanban board stage: if departure date is set but arrival hasn't happened → 'In Transit'
- Payment terms with 'days after departure' trigger: Payment due = Departure Date + trigger days
- Visual timeline display`,
    affects: ["Supplier Orders → Kanban board stage", "Cash Calendar → Payment due dates (departure-triggered)", "Dashboard → Cashflow Chart"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Container Batch", name: "In-Stock Days (buffer)",
    what: "Number of extra days after arrival before the stock is actually available for sale (customs clearance, prep time, etc.).",
    type: "Number (days)",
    auto: "No — defaults to 0.",
    calc: `Effective in-stock date = Arrival Date + In-Stock Days

Example: Arrival April 15. In-Stock Days = 5 (customs clearance).
Stock becomes available on April 20. The stock jump appears on April 20 in the chart, not April 15.`,
    affects: ["Targets → Stock projection (units added on later date)", "Targets → Diamond markers on chart"],
    overriddenBy: ["Explicit In-Stock Date — if you set a specific date, it overrides arrival + buffer"],
    overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Container Batch", name: "In-Stock Date (explicit)",
    what: "Override the arrival + buffer calculation with a specific date when stock will be available.",
    type: "Date (DD.MM.YYYY)",
    auto: "No.",
    calc: "When set, this date is used directly instead of Arrival Date + In-Stock Days. The stock jump appears on this exact date.",
    affects: ["Targets → Stock projection (units added on this date)", "Targets → Diamond markers"],
    overriddenBy: ["Nothing — this is the highest priority for stock availability date"],
    overrides: ["Arrival Date + In-Stock Days calculation"],
    warn: "" },

  { page: "Supplier Orders", section: "Payment Terms", name: "Payment Term Trigger",
    what: "What event determines when this payment is due.",
    type: "Dropdown: On order date / Days after order / Days after departure / Days after arrival / Manual date",
    auto: "No.",
    calc: `Each trigger type calculates the due date differently:
- On order date: due immediately when order is placed
- Days after order: due date = order date + N days
- Days after departure: due date = departure date + N days
- Days after arrival: due date = arrival date + N days
- Manual date: you enter the exact due date

Example: Trigger 'Days after arrival', days = 30, arrival April 15.
Due date = May 15.`,
    affects: ["Cash Calendar → When the payment expense appears", "Dashboard → Cashflow Chart (expense timing)", "Dashboard → Next Payment Due KPI"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Supplier Orders", section: "Order Card", name: "Marketplace Link",
    what: "Which marketplace(s) this order's inventory is destined for.",
    type: "Multi-select checkboxes",
    auto: "No.",
    calc: `Links this order to specific marketplaces so:
- Stock arrivals only appear in the selected marketplace's stock projection
- Lead time is calculated only from orders linked to the selected marketplace
- Cash Projected in Orders KPI is filtered by marketplace

Example: Order linked to Amazon only → stock appears under Amazon's pool, not Shopify's.`,
    affects: ["Targets → Stock projection (filtered by marketplace)", "Targets → Lead time (per-marketplace)", "Targets → Diamond markers (filtered)", "Marketplaces → Cash Projected in Orders"],
    overriddenBy: [], overrides: [], warn: "Unlinked orders (no marketplace selected) appear for all marketplaces." },

  // ═══════════════════════════════════════════════════════════════════════════
  // RECURRING TRANSACTIONS — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Recurring", section: "Transaction Card", name: "Transaction Name",
    what: "Label for this recurring transaction (e.g., 'Office Rent', 'VA Salary', 'Shopify Subscription').",
    type: "Text",
    auto: "No.",
    calc: "No calculation — identification and display only.",
    affects: ["Cash Calendar → Transaction label"], overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Category",
    what: "Type of transaction for organizational purposes.",
    type: "Dropdown: General / Employee / Software / Agency / Prep / Credit / VAT / Custom",
    auto: "No.",
    calc: "No direct calculation — used for filtering and grouping on the Recurring page.",
    affects: ["Recurring → Filter by category"], overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Schedule Type",
    what: "How often this transaction occurs.",
    type: "Dropdown: Monthly / Specific Days / Interval / Trigger Threshold",
    auto: "No.",
    calc: `Monthly: occurs once per month on the specified day, in selected months.
Specific Days: occurs on specific dates you list.
Interval: repeats every N days from the start date.
Trigger Threshold: fires when a cumulative metric hits a value.

Each occurrence creates an expense (or income) entry on your Cash Calendar.`,
    affects: ["Cash Calendar → Which dates the transaction appears on", "Dashboard → Cashflow Chart (recurring expense/income line)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Active Months",
    what: "Which months of the year this transaction is active. Inactive months have no transaction entry.",
    type: "12 checkboxes (Jan-Dec)",
    auto: "No — all months active by default.",
    calc: `The transaction only generates Cash Calendar entries for checked months.

Example: A seasonal employee paid only June through September → check Jun, Jul, Aug, Sep.
No salary expense appears in the other 8 months.`,
    affects: ["Cash Calendar → Transaction only appears in active months", "Dashboard → Cashflow Chart"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Start / End Date",
    what: "The date range during which this recurring transaction is active.",
    type: "Two dates (start and optional end)",
    auto: "No.",
    calc: "No transactions are generated before the start date or after the end date. Leave end date empty for ongoing transactions.",
    affects: ["Cash Calendar → Transaction only appears within this range", "Dashboard → Cashflow Chart"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Transaction Card", name: "Day of Month",
    what: "Which day of the month this transaction occurs (for monthly schedule type).",
    type: "Number (1-31)",
    auto: "No.",
    calc: "The transaction appears on this day in the Cash Calendar. If the month has fewer days (e.g., day 31 in a 30-day month), it falls on the last day of the month.",
    affects: ["Cash Calendar → Transaction date within each month", "Dashboard → Cashflow Chart (daily position)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Recurring", section: "Multi-Brand", name: "Shared Brands Split",
    what: "How this transaction's cost is split across multiple brands when shared.",
    type: "Percentage per brand",
    auto: "No.",
    calc: `When a transaction is shared across brands, each brand gets its allocated percentage.

Example: Office rent $3,000 shared 60/40 between Brand A and Brand B.
Brand A sees $1,800/month. Brand B sees $1,200/month.`,
    affects: ["Cash Calendar → Amount shown per brand", "Dashboard → Cashflow Chart per brand"],
    overriddenBy: [], overrides: [], warn: "Percentages across all brands should add up to 100%." },

  // ═══════════════════════════════════════════════════════════════════════════
  // CASH CALENDAR
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Cash Calendar", section: "Add Transaction", name: "Manual Transaction",
    what: "A one-time income or expense entry — a refund, an ad-hoc bill, a bonus, a loan, or any manual entry.",
    type: "Amount + Date + Description + Income/Expense toggle",
    auto: "No.",
    calc: `Income entries are added to your cash balance on that date.
Expense entries are subtracted from your cash balance.

These appear alongside your recurring items, order payments, and marketplace payouts in the monthly breakdown.`,
    affects: ["Dashboard → Cashflow Chart (one-time income or expense)", "Cash Calendar → Monthly income or expense total"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Cash Calendar", section: "Transaction Row", name: "Mark as Paid",
    what: "Toggle a transaction as paid or unpaid. Paid items show a green checkmark.",
    type: "Toggle button",
    auto: "No.",
    calc: `Marking as paid is a tracking feature — it confirms the transaction actually happened.
Unpaid items may trigger overdue payment alerts in the notification system.

Paid status does NOT change the cashflow projection — the projected amount is used regardless.`,
    affects: ["Cash Calendar → Visual indicator (checkmark)", "Notifications → Overdue payment alerts (unpaid items past due date trigger warnings)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Cash Calendar", section: "Transaction Row", name: "Partial Payment",
    what: "Record a partial payment for a transaction — the amount actually paid so far.",
    type: "Currency amount",
    auto: "No.",
    calc: `When you enter a partial payment, the Cash Calendar shows:
- Paid so far: the amount you entered
- Remaining: original amount - paid amount

The remaining amount stays as an outstanding obligation.`,
    affects: ["Cash Calendar → Paid/remaining breakdown display"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Cash Calendar", section: "Transaction Row", name: "Delay Payment",
    what: "Move a transaction to a later date — by a specific date, number of days, or number of months.",
    type: "Date OR number of days OR number of months",
    auto: "No.",
    calc: `Three modes:
- Date: move to a specific new date
- Days: move forward by N days from the original date
- Months: move forward by N months

The transaction disappears from its original date and appears on the new date.
This affects your cashflow — the expense shifts later, improving near-term balance but creating a future obligation.`,
    affects: ["Cash Calendar → Transaction moves to new date", "Dashboard → Cashflow Chart (expense shifts later)", "Dashboard → Next Payment Due (may change)"],
    overriddenBy: [], overrides: ["Original due date"],
    warn: "" },

  // ═══════════════════════════════════════════════════════════════════════════
  // PLANNER
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Planner", section: "Decision Card", name: "Decision Name",
    what: "The name of the business decision you're planning (e.g., 'Launch new product', 'Hire VA', 'Switch 3PL').",
    type: "Text",
    auto: "No.",
    calc: "No calculation — identification and display on the timeline.",
    affects: ["Planner → Timeline labels", "Notifications → Decision alerts use this name"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Planner", section: "Decision Card", name: "Decision Date",
    what: "When this decision takes effect. Can be a specific day, a month, or a quarter.",
    type: "Date / Month / Quarter selector",
    auto: "No.",
    calc: `The date determines:
- Where the decision appears on the Planner timeline
- When financial impacts start (modifiers, expenses, income from this date forward)
- When overdue alerts trigger (if the date passes and status isn't 'done')`,
    affects: ["Planner → Timeline position", "Modifiers → Start date for linked modifiers", "Notifications → Overdue decision alerts"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Planner", section: "Decision Card", name: "Decision Status",
    what: "The current state of this decision.",
    type: "Dropdown: Planned / In Progress / Done / Cancelled",
    auto: "No.",
    calc: `Status affects notifications:
- 'Planned' past its date → triggers overdue alert
- 'Done' or 'Cancelled' → no alerts, decision considered resolved

The financial impact applies regardless of status (unless the decision is cancelled and you manually remove the linked modifier/expense).`,
    affects: ["Planner → Card visual state", "Notifications → Overdue decision alerts (only for non-done/cancelled)"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Planner", section: "Financial Impact", name: "Impact Type",
    what: "What kind of financial effect this decision has on your cashflow.",
    type: "Dropdown: Payout % change / Units % change / Stop selling / New expense / One-time cost / Cash in / Loan",
    auto: "No.",
    calc: `Each type creates different objects:
- Payout % / Units %: creates a Modifier that adjusts income or targets for selected months
- Stop selling: creates a Units Modifier set to -100% (complete halt)
- New expense: creates a Recurring Transaction starting from the decision date
- One-time cost: adds a single expense entry on the decision date
- Cash in: adds a single income entry on the decision date
- Loan: adds lump sum income + creates recurring repayment expense

All generated items are linked to this decision — deleting the decision removes them.`,
    affects: ["Modifiers page → Linked modifier (for % changes)", "Recurring page → Linked recurring item (for expenses)", "Cash Calendar → One-time entries (for cost/cash-in)", "Dashboard → Cashflow Chart (all of the above flow into projections)"],
    overriddenBy: [], overrides: [], warn: "Deleting the decision also deletes its linked financial impacts." },

  // ═══════════════════════════════════════════════════════════════════════════
  // MODIFIERS — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Modifiers", section: "Modifier Card", name: "Modifier Type",
    what: "What aspect of your finances this modifier adjusts.",
    type: "Dropdown: Payout / Units / Fulfilment Cost / Partner Cost / Triggered / Per-Order / Per-Container",
    auto: "No.",
    calc: `Each type affects a different calculation:
- Payout: changes income per unit (e.g., +15% Black Friday premium)
- Units: changes how many units are sold (e.g., -40% stockout period)
- Fulfilment Cost: changes delivery/warehouse costs (e.g., +12% Q4 FBA surcharge)
- Partner Cost: changes partner/company costs (e.g., +8% rent increase)
- Triggered: fires a one-time income or expense when cumulative units hit a threshold
- Per-Order: fixed cost per purchase order placed
- Per-Container: fixed cost per shipping container`,
    affects: ["Dashboard → Cashflow Chart", "Cash Calendar → Modified amounts", "Targets → Adjusted target bars and stock projection"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Modifiers", section: "Modifier Card", name: "Months Applied",
    what: "Which months this modifier is active. The adjustment only applies during selected months.",
    type: "Month/Year checkboxes or date range",
    auto: "No.",
    calc: `The modifier's percentage or amount is only applied to cashflow calculations for the selected months.

Example: +12% Fulfilment Cost modifier for Oct, Nov, Dec only → Q4 FBA fee surcharge.
January returns to normal costs.`,
    affects: ["Dashboard → Cashflow Chart (only affected months change)", "Cash Calendar → Only selected months show modified amounts", "Targets → Only selected months show modified target bars"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Modifiers", section: "Modifier Card", name: "Modifier Scope",
    what: "Whether this modifier applies to all marketplaces/products, one marketplace, or one specific product.",
    type: "Buttons: All / One Marketplace / One Product",
    auto: "No.",
    calc: `Scope determines which income/cost streams are affected:
- All: every marketplace and product gets the adjustment
- One Marketplace: only the selected marketplace's income/costs change
- One Product: only that specific product's income/costs change

Example: -20% units modifier scoped to Amazon only → Amazon sales drop 20% while Shopify stays normal.`,
    affects: ["Same as modifier value — but filtered to the selected scope"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Modifiers", section: "Modifier Card", name: "Triggered Threshold",
    what: "For triggered modifiers: the cumulative unit count that activates this modifier.",
    type: "Number (units)",
    auto: "No.",
    calc: `The app tracks cumulative units sold from the start of the modifier's active period.
When cumulative sales hit this number, the modifier fires once.

Example: Threshold = 5,000 units. Modifier = $2,000 income (rebate).
When your cumulative sales across active months reach 5,000 units, $2,000 is added to your income.`,
    affects: ["Dashboard → Cashflow Chart (income or expense when threshold hit)", "Cash Calendar → One-time entry when triggered"],
    overriddenBy: [], overrides: [], warn: "Fires only once per active period." },

  // ═══════════════════════════════════════════════════════════════════════════
  // SETTINGS — remaining fields
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Settings", section: "Display & Format", name: "Unit System",
    what: "Whether to show container volumes in cubic meters (metric) or cubic feet (imperial).",
    type: "Toggle: Metric (CBM) / Imperial (ft³)",
    auto: "No.",
    calc: "Display-only conversion: 1 CBM = 35.3147 ft³. Changes the units shown on container volume displays. Does not change any stored values.",
    affects: ["Supplier Orders → Container volume display"],
    overriddenBy: [], overrides: [], warn: "This only changes the display — no values are converted." },

  { page: "Settings", section: "Display & Format", name: "Week Start Day",
    what: "Which day of the week is considered the first day (affects weekly chart aggregation).",
    type: "Button group: Mon / Tue / Wed / Thu / Fri / Sat / Sun",
    auto: "No.",
    calc: "Changes how weekly data is grouped on the Dashboard chart when viewing by week. A week starts on this day and ends 6 days later.",
    affects: ["Dashboard → Weekly chart aggregation"],
    overriddenBy: [], overrides: [], warn: "" },

  // ═══════════════════════════════════════════════════════════════════════════
  // NOTIFICATIONS (Profile page)
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Notifications", section: "Channels", name: "WhatsApp Alerts Toggle",
    what: "Master toggle for receiving alerts and daily digest via WhatsApp.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "When off, no WhatsApp messages are sent even if WhatsApp is connected. Individual alert types are still controlled by their own toggles.",
    affects: ["WhatsApp → Daily digest messages", "WhatsApp → Alert notifications"],
    overriddenBy: [], overrides: [], warn: "You must also connect WhatsApp on the Connect AI tab for messages to be delivered." },

  { page: "Notifications", section: "Channels", name: "Telegram Alerts Toggle",
    what: "Master toggle for receiving alerts and daily digest via Telegram.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "When off, no Telegram messages are sent even if Telegram is connected.",
    affects: ["Telegram → Daily digest messages", "Telegram → Alert notifications"],
    overriddenBy: [], overrides: [], warn: "You must also connect Telegram on the Connect AI tab." },

  { page: "Notifications", section: "Alert Types", name: "Payment Due Reminders",
    what: "Get notified when supplier order payments are coming due.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "When on, you receive alerts via your enabled channels (email, WhatsApp, Telegram) when a payment is due within 7 days.",
    affects: ["Bell icon → Payment due alerts", "WhatsApp/Telegram → Payment due messages", "Email → Payment reminder emails"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Notifications", section: "Alert Types", name: "Low Balance Warning",
    what: "Get notified when your projected cash balance is approaching or below zero.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "Triggers when your projected daily balance drops below the buffer threshold within the next 7/30/90 days (configurable in Settings).",
    affects: ["Bell icon → Balance alerts", "WhatsApp/Telegram → Low balance messages"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Notifications", section: "Alert Types", name: "Stock Low / Stockout",
    what: "Get notified when inventory is running low or projected to run out.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "Triggers when your stock projection shows stockout within the reorder lead time, or when stock is critically low with no incoming orders.",
    affects: ["Bell icon → Stock alerts", "WhatsApp/Telegram → Stock warning messages"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Notifications", section: "Alert Types", name: "Planner Decisions",
    what: "Get notified about upcoming and overdue planner decisions.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "Triggers when a planned decision's date is approaching (within 30 days) or has passed without being marked as done.",
    affects: ["Bell icon → Decision alerts", "WhatsApp/Telegram → Decision reminder messages"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Notifications", section: "Summaries", name: "Daily Digest",
    what: "Receive a morning summary of your cash balance, upcoming payments, and active alerts.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "Sent once daily (morning) via your enabled channels. Includes: today's balance, next 3 payments due, runway status, and any critical alerts.",
    affects: ["WhatsApp/Telegram → Daily morning message"],
    overriddenBy: [], overrides: [], warn: "" },

  { page: "Notifications", section: "Summaries", name: "Weekly Summary",
    what: "Receive an end-of-week performance recap with trends.",
    type: "Toggle (on/off)",
    auto: "No.",
    calc: "Sent once weekly via email. Includes: week's income vs expenses, target hit rate, stock status, and week-over-week trends.",
    affects: ["Email → Weekly summary email"],
    overriddenBy: [], overrides: [], warn: "" },

  // ═══════════════════════════════════════════════════════════════════════════
  // IMPORTANT BEHAVIORAL NOTES (from audit)
  // ═══════════════════════════════════════════════════════════════════════════
  { page: "Marketplaces", section: "Product Card (Advanced)", name: "Ad Cost Toggles (mutual exclusion)",
    what: "Two ad cost options exist: 'Deduct from payout projection' and 'Generate ad spend as expenses'. These are mutually exclusive — enabling one automatically disables the other.",
    type: "Toggle pair",
    auto: "No.",
    calc: `'Deduct from projection' reduces the income shown on Dashboard and Cash Calendar. Your payout/unit goes from $10 to $8 (if ad cost is $2).

'Generate as expenses' creates expense line items on Cash Calendar for the ad spend amount. Your payout stays at $10 but a $2 expense appears.

Both accomplish the same goal (accounting for ad cost) but through different mechanisms. Using both at once would double-count the ad cost — so enabling one disables the other.`,
    affects: ["Dashboard → Cashflow Chart income (if deduct)", "Cash Calendar → Expense items (if generate)"],
    overriddenBy: [], overrides: [],
    warn: "These are mutually exclusive. The app enforces this automatically." },

  { page: "Marketplaces", section: "Inventory Pools", name: "Stock Override to Zero",
    what: "You can override stock to exactly 0 units. The override flag (not the value) determines if the override is active.",
    type: "Number + boolean flag",
    auto: "No.",
    calc: `When you set stock override to 0 and the override is active, the app respects this as 'I have zero stock' — it won't fall through to API-synced values.

Previously, setting 0 would be ignored (treated as 'no override set'). Now the boolean flag is what matters.`,
    affects: ["Dashboard → Money in Inventory (shows 0)", "Targets → Stock projection starts from 0", "Targets → Stockout shows immediate"],
    overriddenBy: ["Pool stock override (higher priority)"], overrides: ["API-synced stock", "Per-product stock"],
    warn: "Set the override value to 0 AND ensure the override flag is enabled." },
];

// ─── KPI DEFINITIONS ────────────────────────────────────────────────────────
export const KPIS = [
  { page: "Dashboard", name: "Health Score",
    what: "A single number (0-100) summarizing the overall financial health of your business.",
    calc: `Starts at 100 and subtracts penalty points based on 5 factors:

1. Runway (up to -30 points):
   More than 90 days of cash → no penalty.
   60-90 days → -5 points.
   30-60 days → -15 points.
   14-30 days → -22 points.
   7-14 days → -27 points.
   Less than 7 days → -30 points.

2. Stockout proximity (up to -25 points):
   More than 90 days of stock → no penalty.
   60-90 days → -5 points.
   30-60 days → -12 points.
   14-30 days → -18 points.
   Less than 14 days → -25 points.

3. Breakeven coverage (up to -25 points):
   Your monthly target covers breakeven units comfortably → no penalty.
   Target barely covers breakeven → partial penalty.
   Target below breakeven → full -25 points.

4. Today's balance relative to buffer (up to -20 points):
   Balance well above buffer → no penalty.
   Balance near or below buffer → increasing penalty.

5. Cash-to-Cash cycle (up to -25 points):
   60 days or less → no penalty.
   90 days → -5 points.
   120 days → -10 points.
   180 days → -15 points.
   More than 365 days → -25 points.

The result is color-coded:
90-100 = Healthy (green)
70-89 = Good (green)
50-69 = Caution (orange)
Below 50 = At Risk (red)`,
    feeds: ["Available Today", "Runway", "Stockout Date", "Monthly Target", "Breakeven Units", "Cash-to-Cash Cycle"],
    withApi: "More accurate — stockout and breakeven use real sales data and synced inventory.",
    withoutApi: "Uses your manually entered targets and stock. Less accurate if data isn't current.",
    missing: "If any feeding value is missing (no stock data, no targets, no balance), that factor gets the maximum penalty — the score drops but still works." },

  { page: "Dashboard", name: "Available Today",
    what: "Your current real bank balance — the starting point for every cashflow projection.",
    calc: `Not calculated — this is a number you enter manually.

Every projection in the app starts from this value:
Day 1 balance = Available Today
Day 2 balance = Day 1 + income received - expenses due
Day 3 balance = Day 2 + income - expenses
...and so on for every day into the future.`,
    feeds: ["You enter this manually"],
    withApi: "Same — API does not sync your bank balance. You must update it yourself.",
    withoutApi: "Same.",
    missing: "If set to 0, all projections start from zero. The cashflow chart starts at the bottom. Set your real bank balance for accurate projections." },

  { page: "Dashboard", name: "Blended Payout/Unit",
    what: "The weighted average income you receive per unit sold, across all marketplaces.",
    calc: `Two-stage weighting:

Stage 1 — per-marketplace effective payout (effectivePayoutForMkt in src/lib/calc/marketplaces.js):
For every product in the marketplace, take its Payout/Unit, then subtract: delivery (only if 'Deduct from payout' is checked), ad cost (per-product → group → marketplace hierarchy), refund (if opted in). Multiply by that product's % of Market and sum across products. Result: marketplace's blended payout/unit.

Stage 2 — marketplace blend (kpiSnapshot.js):
Sum across marketplaces of (marketplace effective payout × marketplace % Total).

Example:
Amazon: products blend to $12/unit × 60% share = $7.20
Shopify: products blend to $18/unit × 40% share = $7.20
Blended = $14.40/unit

When a single marketplace is selected, only that marketplace's blend is shown.`,
    feeds: ["Per-product Payout/Unit", "Per-product % of Market", "Per-product 'Deduct from payout' delivery flag", "Ad cost (product → group → marketplace hierarchy)", "Refund deduction toggle", "Marketplace % Total"],
    withApi: "Uses API-synced payout per unit (calculated from real settlement data). More accurate.",
    withoutApi: "Uses your manually entered Payout/Unit per product.",
    missing: "If no marketplaces exist → shows 'No marketplaces configured'. If no products → shows 'No products yet'. If all payouts are 0 → shows '$0' with a warning." },

  { page: "Dashboard", name: "Runway",
    what: "How many days your cash will last before the balance drops below your safety buffer (or zero if no buffer is set).",
    calc: `The app walks forward day by day from today:

Starting balance = Available Today
Each day: add projected income, subtract projected expenses.
Stop when balance drops below the buffer threshold.
Count the days — that's your runway.

Income per day comes from: monthly target × marketplace share × payout per unit, divided by days in the month, shifted by payout delay.

Expenses per day come from: recurring transactions + supplier order payments + delivery costs + ad spend — all placed on their specific due dates.

If the balance never drops below the buffer → runway shows as "365+ days" or infinity.

Example: Available Today is $50,000. Net daily burn is $500/day. Buffer is $5,000.
Runway = ($50,000 - $5,000) / $500 = 90 days.`,
    feeds: ["Available Today", "Monthly Targets", "All marketplace payouts and % shares", "All recurring transactions", "All supplier order payment terms", "Delivery costs", "Payout delays", "Buffer threshold (Settings)"],
    withApi: "Income projections use API-synced payout data and actual sold numbers for accuracy.",
    withoutApi: "Uses your manually entered targets and payouts.",
    missing: "If no targets or payouts are set, projected income is 0 and runway depends only on expenses draining your balance." },

  { page: "Dashboard", name: "Current Month",
    what: "Your sales performance this month — actual units sold vs. target, with a pace indicator.",
    calc: `The app calculates:

Target: your monthly target × marketplace share (if a specific marketplace is selected).

Actual: units sold so far this month (from API sync or manual entry in Targets).

Pace: extrapolates your current selling speed to the end of the month.
Pace = (actual sold so far / days elapsed) × days in month.

Progress bar = actual / target × 100%.

Status label:
- "On Track" — pace is at or above target
- "Behind" — pace is 70-99% of target
- "Off Track" — pace is below 70% of target`,
    feeds: ["Monthly Target (Targets page)", "Actual Sold (Targets page or API sync)", "Today's date (for pace calculation)"],
    withApi: "Actual sold is synced from marketplace APIs automatically. More accurate pace.",
    withoutApi: "You enter actual sold manually on the Targets page. Pace calculation works the same.",
    missing: "If no target is set → shows 'No target'. If no actual sold data → shows 0 units sold with no pace calculation." },

  { page: "Dashboard", name: "Money in Inventory",
    what: "The total value of your physical inventory — units in stock multiplied by their cost per unit, plus the value of goods in transit from suppliers.",
    calc: `Money in Inventory = (units in stock × cost per unit) + (value of in-transit goods)

Units in stock comes from the inventory pool hierarchy:
1. Pool stock override (if manually set)
2. Per-marketplace stock (API-synced or manual)
3. Per-product stock (summed across products)

Cost per unit comes from the cost hierarchy:
1. Pool cost per unit
2. Marketplace Avg COGS/unit
3. Product Group COGS
4. Per-product COGS breakdown (Product Cost + Shipping + Customs + Prep)

In-transit value = orders where goods have been paid for but haven't arrived yet.

Example: 5,000 units × $8/unit = $40,000 in stock. Plus $12,000 in-transit. Total = $52,000.

When you select a specific marketplace, it shows only that marketplace's inventory (from its pool).`,
    feeds: ["Inventory Pool stock", "Cost per unit (pool → marketplace → group → product hierarchy)", "Supplier Orders (for in-transit value)", "Marketplace selection"],
    withApi: "Stock levels sync automatically from marketplace APIs. Cost still needs manual entry.",
    withoutApi: "You set stock manually on the Inventory Pools section of Marketplaces page.",
    missing: "If no stock data → shows 'No stock data' with instructions. If stock exists but no cost is set → shows '$0' with a 'COG missing for X units' warning badge." },

  { page: "Dashboard", name: "Stockout Date",
    what: "The projected date when your inventory will run out completely — zero units left.",
    calc: `The app walks forward day by day from today:

Starting stock = current inventory (from pool hierarchy).
Each day: subtract daily sales rate (monthly target / days in month).
Each order arrival: add batch units (minus shrinkage %).
Stop when stock hits zero — that's the stockout date.

When you select a specific marketplace, the daily sales rate is scaled by that marketplace's % Total. Stock comes from that marketplace's pool.

If stock never hits zero within 365 days → shows "365+ days".

Example: 5,000 units in stock. Selling 100/day. No orders arriving.
Stockout = 5,000 / 100 = 50 days from today.`,
    feeds: ["Inventory Pool stock", "Monthly Targets", "Marketplace % Total (for per-marketplace view)", "Supplier Order arrival dates and unit counts", "Shrinkage %"],
    withApi: "Stock is API-synced. Sales rate from actual data is more accurate.",
    withoutApi: "Uses manually entered stock and targets.",
    missing: "If no stock data → shows 'No data — can't project stockout'. If no targets → stock never depletes, shows '365+ days'." },

  { page: "Dashboard", name: "Cash-to-Cash Cycle",
    what: "The total number of days from paying your supplier to receiving cash from the sale of those goods.",
    calc: `Cash-to-Cash = Production + Shipping + Prep + Selling + Payout Delay

Each stage is estimated from your data:

Production days = 60% of your average lead time (from order history).
Example: Average lead time 90 days → production = 54 days.

Shipping days = 40% of your average lead time.
Example: 90 days average → shipping = 36 days.

Prep days = 7 days (fixed estimate for customs clearance and prep).

Selling days = your "days of stock" (how many days your current inventory will last at your current selling rate).

Payout delay days = weighted average payout delay across your marketplaces.

Example: Production 54 + Shipping 36 + Prep 7 + Selling 45 + Payout 14 = 156 days total.
That means it takes 156 days from the moment you pay your supplier until you receive cash from selling those goods.

Color: Green (60 days or less), Orange (61-120 days), Red (over 120 days).`,
    feeds: ["Supplier Orders (for lead time calculation)", "Inventory Pool stock (for days of stock)", "Monthly Targets (for selling rate)", "Marketplace Payout Delays"],
    withApi: "Lead times, stock, and payout delays are more accurate with real data.",
    withoutApi: "Uses manually entered values. Lead time defaults to 90 days if no orders exist.",
    missing: "If all three sources are missing (no orders, no stock, no payout delays) → shows a 'Missing data' warning. Each missing source uses a fallback default." },

  { page: "Dashboard", name: "Reserve Held",
    what: "Money being held by your marketplaces that hasn't been released to you yet.",
    calc: `For each marketplace, the reserve amount comes from one of three sources (first one that has a value wins):

1. Manual override — you typed in a dollar amount on the Marketplaces page.
2. API sync — Amazon/Shopify report their reserve amount automatically.
3. Rate-based estimate — you set a Reserve Rate percentage, and the app multiplies it by your last 30 days of API-sourced payouts from that marketplace (manually-entered income items are excluded from the estimate base).

The total Reserve Held is the sum across all selected marketplaces.

Example:
Amazon: $3,500 (from API sync) — badge shows "API"
eBay: $800 (from 5% rate × $16,000 recent payouts) — badge shows "~5%"
Total: $4,300

This is NOT deducted from your cashflow — reserves are temporary holds that will eventually be released. It's shown so you know how much cash is "locked up" by the platforms.`,
    feeds: ["Reserve Held (manual, per marketplace)", "Reserve Rate (% per marketplace)", "API-synced reserve amounts (Amazon/Shopify)", "Recent 30-day payout data"],
    withApi: "Amazon and Shopify report exact reserve amounts. Much more accurate.",
    withoutApi: "Set Reserve Rate % on the marketplace and the app estimates from recent payouts. Or enter a manual amount.",
    missing: "If no reserve data is set and no API is connected → shows $0 with no warning." },

  { page: "Dashboard", name: "Next Payments",
    what: "The next upcoming payment obligations — supplier order payments, recurring expenses, and delivery costs.",
    calc: `The app collects all future payment dates from:

1. Supplier order payment terms — each term has a due date (calculated from trigger type).
2. Recurring transactions — next occurrence based on schedule type and active months.
3. Delivery cost invoices — monthly delivery expenses.

These are sorted by date and the next 3 are shown.

The "Overdue" badge counts payments that are past their due date and not yet marked as paid on the Cash Calendar.

When you select a specific marketplace, only payments linked to that marketplace are shown.`,
    feeds: ["Supplier Order payment terms (dates and amounts)", "Recurring Transactions (next occurrence)", "Delivery costs", "Mark-as-paid status from Cash Calendar"],
    withApi: "Same — payment schedules are manual regardless of API.",
    withoutApi: "Same.",
    missing: "If no orders, no recurring transactions, and no delivery costs → shows 'No upcoming payments'." },

  // ── TARGETS PAGE KPIs ──
  { page: "Targets", name: "Current Month Target",
    what: "This month's unit sales target, adjusted for marketplace selection.",
    calc: `When viewing all marketplaces: shows the brand-wide monthly target as entered on the month card.

When viewing a single marketplace: shows that marketplace's share.
Marketplace target = brand target × marketplace % Total.
Unless you've set a per-marketplace override — then the override value is used directly.

Example: Brand target 10,000. Amazon is 60%.
Amazon target = 6,000 (or your override if set).`,
    feeds: ["Monthly Target (month card)", "Marketplace % Total", "Per-marketplace target override (if set)"],
    withApi: "Same — targets are always manual.", withoutApi: "Same.",
    missing: "If no target → shows 0 with 'No target' status." },

  { page: "Targets", name: "Year Total",
    what: "Sum of all monthly targets for the selected year, adjusted for marketplace selection.",
    calc: `Adds up all 12 monthly targets for the year.

When viewing a single marketplace: each month uses the marketplace's resolved target (override or calculated share).

The hit rate shows actual sold / year total as a percentage.
The pace indicator compares actual progress vs. expected progress through the year (prorated by how far into the year we are).`,
    feeds: ["All 12 Monthly Targets", "Actual Sold (per month)", "Marketplace selection"],
    withApi: "Actual sold synced from API improves hit rate accuracy.", withoutApi: "Enter actual sold manually.",
    missing: "If no targets set → shows 0." },

  { page: "Targets", name: "Stockout KPI (Targets)",
    what: "Days until stockout, with color coding. Same calculation as Dashboard Stockout but within the Targets page context.",
    calc: `Same daily-walk projection as the Dashboard Stockout Date. Starts from the pool's current stock, depletes by daily sales rate, adds order arrivals.

When simulation is active, uses the sim-adjusted targets for a what-if view.

Color: Red (less than lead time days), Orange (less than lead time + 14 days), Green (more than lead time + 30 days).`,
    feeds: ["Pool stock", "Monthly Targets", "Order arrivals", "Shrinkage %", "Lead time"],
    withApi: "Stock from API sync. Same projection logic.", withoutApi: "Manual stock.",
    missing: "If no stock data → shows 0 days in red." },

  { page: "Targets", name: "Reorder KPI",
    what: "How many days until you must place a new order to avoid running out of stock. Calculated as stockout date minus lead time.",
    calc: `Reorder deadline = Stockout Date - Lead Time

Lead time is calculated from your order history:
The app looks at each past order's order date and arrival date, calculates the difference, and uses the most recent order's lead time.

When you select a specific marketplace, only orders linked to that marketplace are used for lead time calculation — not a brand-wide average.

If you've set a custom lead time override, that value is used instead.

Example: Stockout June 3. Lead time 60 days (from last Amazon order).
Reorder by = June 3 - 60 days = April 4.
If today is April 10, the KPI shows "-6 days" (overdue!).`,
    feeds: ["Stockout Date", "Lead time (from order history, filtered by marketplace)", "Custom lead time override", "Stockout buffer days"],
    withApi: "Same.", withoutApi: "Same.",
    missing: "If no orders exist → lead time defaults to 90 days. If no stock data → shows 'No data'." },
];

// ─── CHART ELEMENT DEFINITIONS ───────────────────────────────────────────────
export const CHARTS = [
  // ── DASHBOARD CHART ──
  { page: "Dashboard", name: "Balance Line (main)",
    what: "The main line on the Dashboard chart — shows your projected cash balance over time.",
    calc: `The app builds a day-by-day projection:

Day 1: Start with your Available Today balance.
Each following day:
  Add income received that day (marketplace payouts arriving after their delay period).
  Subtract expenses due that day (supplier payments, recurring items, delivery costs).
  New balance = previous balance + income - expenses.

The line goes up when income exceeds expenses (payouts arriving), and down when expenses exceed income (supplier payments due).

Income for each day = units sold that day × payout per unit for each marketplace.
Units per day = monthly target / days in the month.
But the CASH arrives later — shifted forward by each marketplace's Payout Delay.

Example: You sell 100 units on April 1 at $10/unit on Amazon (14-day delay).
The $1,000 income shows on April 15, not April 1.`,
    feeds: ["Available Today", "Monthly Targets", "All marketplace payouts and % shares", "Payout Delays", "All recurring transactions", "Supplier order payment terms", "Delivery costs", "Modifiers"],
    visibility: "Always visible on Dashboard chart." },

  { page: "Dashboard", name: "Zero Line",
    what: "A horizontal dashed red line at $0 — marks the break-even point.",
    calc: "Fixed at y = 0. When the balance line crosses below this, you're in negative cash territory.",
    feeds: [],
    visibility: "Always visible." },

  { page: "Dashboard", name: "Buffer Threshold Line",
    what: "A horizontal dashed red line at your configured safety buffer amount.",
    calc: `Drawn at the dollar amount you've set as your minimum safe cash balance (configured in Settings → Alert Thresholds → Buffer Threshold).

When the balance projection crosses below this line, the Runway KPI starts counting down.`,
    feeds: ["Buffer Threshold (Settings)"],
    visibility: "Only visible when buffer threshold is set and greater than 0." },

  { page: "Dashboard", name: "Today Line (Dashboard)",
    what: "A vertical dashed green line marking today's date on the chart.",
    calc: "Positioned at today's date on the horizontal axis. Everything to the left is past, everything to the right is projected future.",
    feeds: [],
    visibility: "Visible in 'From January' and 'All time' views. Hidden in 'From today' view (today is already at the left edge)." },

  { page: "Dashboard", name: "Scenario Lines (Conservative / Optimistic)",
    what: "Two additional dashed lines showing best-case and worst-case cash projections when the scenario simulator is open.",
    calc: `The scenario simulator applies percentage adjustments to your projected income and expenses:

Conservative (red dashed): income reduced, expenses increased — shows what happens if things go worse than planned.
Optimistic (green dashed): income increased, expenses reduced — shows the best-case scenario.

The main balance line stays unchanged for comparison.`,
    feeds: ["Scenario % adjustments (from the scenario toggle)"],
    visibility: "Only visible when the scenario simulator is open." },

  { page: "Dashboard", name: "Decision Dots",
    what: "Emoji icons connected to the balance line by dashed stems — each represents a Planner decision.",
    calc: `Each decision from the Planner page gets a dot at its scheduled date:

Position: horizontally at the decision's date, vertically offset above or below the balance line to avoid overlap.

The dot shows the decision's emoji icon. Multiple decisions on the same date are stacked vertically.

If more than 5 decisions cluster at one point, they collapse into a single badge showing the count.

Clicking a decision dot navigates to the Planner page.`,
    feeds: ["Planner Decisions (name, date, icon, status)"],
    visibility: "Only visible when 'Planner' toggle is enabled in the chart controls." },

  { page: "Dashboard", name: "Order Payment Markers (Diamonds)",
    what: "Diamond-shaped markers showing when supplier order payments are due, with their status (paid, overdue, pending).",
    calc: `Each payment term from the selected order gets a diamond at its due date:

Color indicates status:
- Green (paid): you've marked it as paid on Cash Calendar
- Red (overdue): due date has passed and not yet paid
- Order color (pending): upcoming payment

Diamonds show the payment type icon: credit card for payments, ship for shipping, customs stamp for customs.

Hovering shows the payment amount.`,
    feeds: ["Supplier Order payment terms (dates, amounts, paid status)", "Selected order filter"],
    visibility: "Only visible when 'Orders' toggle is enabled and an order is selected." },

  { page: "Dashboard", name: "Year Boundary Lines",
    what: "Vertical dashed orange lines marking the transition between calendar years.",
    calc: "Drawn at January 1st of each year within the chart's visible range.",
    feeds: [],
    visibility: "Visible when chart spans multiple years." },

  // ── TARGETS CHART ──
  { page: "Targets", name: "Target Line (dashed purple)",
    what: "Shows your planned unit targets for each month or week.",
    calc: `Each bar represents the target for that period:

Month view: the monthly target value (from the Targets month cards).
Week view: monthly target × 7 / days in the month (distributes monthly target evenly across weeks).

When a single marketplace is selected: target = brand target × marketplace % Total (or the per-marketplace override if set).

When simulation is active: target = base target × (1 + sim% / 100).`,
    feeds: ["Monthly Targets", "Marketplace % Total", "Per-marketplace target overrides", "Simulation %"],
    visibility: "Always visible on Targets chart." },

  { page: "Targets", name: "Actual Line (solid green)",
    what: "Shows real units sold — actual sales performance vs. your targets.",
    calc: `For past months: shows the Actual Sold value (from API sync or manual entry on the Targets month cards).

For the current month: shows actual sold so far.

For future months: no data — the line stops.

In week view: monthly actual is distributed evenly across weeks in that month (monthly actual × 7 / days in month).`,
    feeds: ["Actual Sold (per month, from Targets or API)"],
    visibility: "Shows data points only for past and current months. No data for future months." },

  { page: "Targets", name: "Stock/Inventory Line (teal dashed)",
    what: "Shows the projected stock level over time — how your inventory depletes as you sell and increases when orders arrive.",
    calc: `The app builds a day-by-day stock projection:

Starting point: current stock from the inventory pool hierarchy (today's date).

For each future day:
  Subtract the daily sales rate = monthly target / days in the month.
  If an order batch arrives on this day, add those units (minus shrinkage %).
  Stock cannot go below zero (floors at 0).

The line shows the end-of-period stock:
Month view: stock at the last day of each month.
Week view: stock at the last day of each week.

For past months (before today): the app reconstructs what stock was by walking BACKWARD from today's known stock.

When simulation is active, the sales rate uses the sim-adjusted targets.`,
    feeds: ["Pool stock (starting point)", "Monthly Targets (depletion rate)", "Supplier Order arrival dates and units", "Shrinkage %", "Simulation % (if active)"],
    visibility: "Only visible when the 'Stock' toggle is enabled in chart controls." },

  { page: "Targets", name: "Reorder Line (orange thick solid)",
    what: "A thick vertical orange line marking the deadline by which you must place a new supplier order to avoid running out of stock.",
    calc: `Reorder Date = Stockout Date - Lead Time

If the reorder date is in the past (you should have ordered already), the line appears at the left edge of the chart with an "Overdue!" label in red.

Multiple reorder lines can appear if stock recovers from an order arrival and then depletes again.

Lead time is calculated from your order history — specifically from orders linked to the selected marketplace (not a brand-wide average). If you've set a custom lead time, that's used instead.`,
    feeds: ["Stockout Date(s)", "Lead time (per-marketplace from order history)", "Custom lead time override"],
    visibility: "Only visible when the 'Stock' toggle is enabled. Does not appear if there is no projected stockout." },

  { page: "Targets", name: "Insufficient Line (orange dashed)",
    what: "A vertical dashed orange line marking when your stock drops below the remaining monthly demand — you won't have enough inventory to fulfill the rest of the month.",
    calc: `Each day, the app compares:
Your current stock vs. the remaining demand for the rest of the month.

Remaining demand = daily sales rate × days left in the month.

When stock drops below remaining demand for the first time, the insufficient line appears at that date.

The line only appears BEFORE the first stockout — once stock hits zero, "insufficient" is redundant.

Multiple lines can appear if stock recovers (order arrives, stock goes above demand) and then drops below again.

Example: May has 31 days. Target 3,100 units (100/day). On May 10, stock = 1,800.
Remaining demand = 100 × 21 days left = 2,100.
1,800 < 2,100 → Insufficient line appears at May 10.`,
    feeds: ["Pool stock", "Monthly Targets", "Order arrivals", "Shrinkage %"],
    visibility: "Only visible when 'Stock' toggle is enabled. Does not appear after the first stockout date." },

  { page: "Targets", name: "Stockout Line (red dashed)",
    what: "A vertical dashed red line marking when your stock hits zero — you're completely out of inventory.",
    calc: `Appears on the exact day when stock drops from above zero to zero (or below).

Multiple stockout lines can appear if:
1. Stock hits zero → line appears
2. An order arrives and stock recovers above zero
3. Stock depletes again and hits zero → second line appears

Each line is labeled "Stockout {date}".`,
    feeds: ["Pool stock", "Monthly Targets", "Order arrivals", "Shrinkage %"],
    visibility: "Only visible when 'Stock' toggle is enabled." },

  { page: "Targets", name: "Zero-Stock Segments (red thick horizontal)",
    what: "Thick red horizontal lines along the bottom of the chart showing periods where stock is at zero for multiple consecutive data points.",
    calc: `When stock stays at zero for 2 or more consecutive months (or weeks in week view), a thick red line is drawn along the zero axis connecting those points.

If stock is zero for exactly one data point, a single red dot is shown instead.`,
    feeds: ["Stock projection data"],
    visibility: "Only visible when 'Stock' toggle is enabled and stock reaches zero." },

  { page: "Targets", name: "Diamond Markers (order arrivals)",
    what: "Diamond-shaped markers showing when supplier order batches arrive and add stock to your inventory.",
    calc: `Each order's container batches get a diamond at their in-stock date.

The diamond position:
Horizontal: the week or month when the batch arrives.
Vertical: at the stock line level for that period.

Color: matches the order's assigned color.

Hovering shows: order name, batch number, unit count, container count, departure date, arrival date, and in-stock date.

When filtering by a specific order, only that order's diamonds are shown.`,
    feeds: ["Supplier Order container batches (arrival/in-stock dates, unit counts)", "Order filter selection"],
    visibility: "Only visible when 'Orders' toggle is enabled in chart controls." },

  { page: "Targets", name: "Today Line (Targets)",
    what: "A vertical dashed green line marking today's date on the chart.",
    calc: "Same as Dashboard. Positioned at today's date. Data to the left is past, to the right is projected.",
    feeds: [],
    visibility: "Always visible when today falls within the chart's date range." },
];

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function DataMap() {
  const T = useTheme();
  const [search, setSearch] = useState("");
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [filterPage, setFilterPage] = useState("all");
  const [tab, setTab] = useState("fields"); // "fields" | "kpis" | "charts"

  const activeData = tab === "kpis" ? KPIS : tab === "charts" ? CHARTS : FIELDS;
  const pages = useMemo(() => [...new Set(activeData.map(f => f.page))], [activeData]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return activeData.filter(f => {
      if (filterPage !== "all" && f.page !== filterPage) return false;
      if (!q) return true;
      return (
        f.name.toLowerCase().includes(q) ||
        f.what.toLowerCase().includes(q) ||
        f.page.toLowerCase().includes(q) ||
        (f.section && f.section.toLowerCase().includes(q)) ||
        (f.affects && f.affects.some(a => a.toLowerCase().includes(q))) ||
        (f.feeds && f.feeds.some(a => a.toLowerCase().includes(q))) ||
        (f.calc && f.calc.toLowerCase().includes(q))
      );
    });
  }, [search, filterPage, activeData]);

  const tagStyle = (color) => ({
    display: "inline-block", fontSize: 10, fontWeight: 700,
    padding: "2px 8px", borderRadius: RADIUS.pill,
    background: color + "15", color, border: `1px solid ${color}40`,
    marginRight: 4, marginBottom: 4,
  });

  const sectionStyle = (label, color) => ({
    fontSize: 11, fontWeight: 800, color, textTransform: "uppercase",
    letterSpacing: 0.5, marginTop: 16, marginBottom: 8,
    paddingBottom: 4, borderBottom: `1px solid ${color}30`,
  });

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", fontFamily: FONT, padding: "0 16px" }}>
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => { window.location.hash = "help"; }}
          style={{
            background:"transparent", border:`1px solid ${T.border}`, color:T.textSub,
            fontSize:12, fontWeight:600, padding:"5px 12px", borderRadius:RADIUS.pill,
            cursor:"pointer", fontFamily:FONT, marginBottom:14, transition:"all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.accent; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.textSub; e.currentTarget.style.borderColor = T.border; }}
        >← Back to Help</button>
        <div style={{ fontSize: 26, fontWeight: 800, color: T.text, letterSpacing: -0.5, marginBottom: 4 }}>
          📚 Data Dictionary
        </div>
        <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6 }}>
          {tab === "fields" ? "Every input field — what it does, how it's calculated, and what it affects."
           : tab === "kpis" ? "Every KPI card — how it's calculated, what feeds it, and what happens when data is missing."
           : "Every chart element — what drives it, how it's drawn, and when it appears."}
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{
        display: "flex", gap: 2, marginBottom: 20,
        background: T.surfaceHi, borderRadius: RADIUS.lg, padding: 3,
      }}>
        {[
          { id: "fields", label: "Input Fields", icon: "📝", count: FIELDS.length },
          { id: "kpis",   label: "KPI Cards",    icon: "📊", count: KPIS.length },
          { id: "charts", label: "Chart Elements",icon: "📈", count: CHARTS.length },
        ].map(t => {
          const isActive = tab === t.id;
          return (
            <button key={t.id} onClick={() => { setTab(t.id); setExpandedIdx(null); setFilterPage("all"); }} style={{
              flex: 1, padding: "10px 12px",
              background: isActive ? T.accent : "transparent",
              color: isActive ? "#fff" : T.textSub,
              border: "none", borderRadius: RADIUS.md,
              fontFamily: FONT, fontSize: 12, fontWeight: isActive ? 700 : 400,
              cursor: "pointer", transition: "all 0.18s",
              boxShadow: isActive ? `0 2px 8px ${T.accent}44` : "none",
              whiteSpace: "nowrap",
            }}>
              {t.icon} {t.label} <span style={{ opacity: 0.7, fontSize: 10 }}>({t.count})</span>
            </button>
          );
        })}
      </div>

      {/* Search + filter */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search fields..."
          style={{
            flex: 1, minWidth: 200, padding: "10px 14px",
            background: T.surface, border: `1.5px solid ${T.border}`,
            borderRadius: RADIUS.md, color: T.text, fontSize: 14,
            fontFamily: FONT, outline: "none",
          }}
        />
        <select
          value={filterPage} onChange={e => setFilterPage(e.target.value)}
          style={{
            padding: "10px 14px", background: T.surface,
            border: `1.5px solid ${T.border}`, borderRadius: RADIUS.md,
            color: T.text, fontSize: 13, fontFamily: FONT, outline: "none",
          }}
        >
          <option value="all">All pages</option>
          {pages.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 16 }}>
        {filtered.length} {tab === "fields" ? "field" : tab === "kpis" ? "KPI" : "element"}{filtered.length !== 1 ? "s" : ""} found
      </div>

      {/* Cards */}
      {filtered.map((f, i) => {
        const globalIdx = activeData.indexOf(f);
        const isOpen = expandedIdx === globalIdx;
        return (
          <div key={globalIdx} style={{
            background: T.card, border: `1px solid ${isOpen ? T.accent + "55" : T.border}`,
            borderRadius: RADIUS.lg, marginBottom: 8,
            transition: "border-color 0.15s",
          }}>
            {/* Header — always visible */}
            <button
              onClick={() => setExpandedIdx(isOpen ? null : globalIdx)}
              style={{
                width: "100%", textAlign: "left", padding: "14px 18px",
                background: "transparent", border: "none", cursor: "pointer",
                fontFamily: FONT, display: "flex", alignItems: "center", gap: 10,
              }}
            >
              <span style={{ fontSize: 9, color: T.textSub, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▶</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{f.name}</div>
                <div style={{ fontSize: 11, color: T.textSub }}>
                  📍 {f.page}{f.section ? ` → ${f.section}` : ""}
                </div>
              </div>
              {f.type && <span style={tagStyle(T.accent)}>{f.type}</span>}
              {f.auto && f.auto.startsWith("Yes") && <span style={tagStyle(T.green)}>Auto</span>}
              {f.visibility && <span style={tagStyle(T.teal || "#06b6d4")}>Chart</span>}
            </button>

            {/* Expanded detail */}
            {isOpen && (
              <div style={{ padding: "0 18px 18px", animation: "sfFadeIn 0.15s ease both" }}>
                <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.7, marginBottom: 12 }}>
                  {f.what}
                </div>

                {f.auto && (
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12, padding: "8px 12px", background: T.surface, borderRadius: RADIUS.sm, lineHeight: 1.6 }}>
                    <strong style={{ color: T.text }}>Auto-calculated:</strong> {f.auto}
                  </div>
                )}

                {f.calc && (<>
                  <div style={sectionStyle("How it's calculated", T.accent)}>How it's calculated</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.8, whiteSpace: "pre-line", marginBottom: 4, padding: "10px 14px", background: T.surface, borderRadius: RADIUS.sm }}>
                    {f.calc}
                  </div>
                </>)}

                {f.affects && f.affects.length > 0 && (<>
                  <div style={sectionStyle("Affects", T.green)}>Affects</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {f.affects.map((a, j) => (
                      <div key={j} style={{ fontSize: 12, color: T.textSub, paddingLeft: 12, position: "relative" }}>
                        <span style={{ position: "absolute", left: 0, color: T.green }}>•</span> {a}
                      </div>
                    ))}
                  </div>
                </>)}

                {f.overriddenBy && f.overriddenBy.length > 0 && (<>
                  <div style={sectionStyle("Overridden by", T.orange)}>Overridden by</div>
                  {f.overriddenBy.map((o, j) => (
                    <div key={j} style={{ fontSize: 12, color: T.textSub, paddingLeft: 12, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: T.orange }}>•</span> {o}
                    </div>
                  ))}
                </>)}

                {f.overrides && f.overrides.length > 0 && (<>
                  <div style={sectionStyle("Overrides", T.blue || "#0071e3")}>Overrides</div>
                  {f.overrides.map((o, j) => (
                    <div key={j} style={{ fontSize: 12, color: T.textSub, paddingLeft: 12, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: T.blue || "#0071e3" }}>•</span> {o}
                    </div>
                  ))}
                </>)}

                {/* KPI-specific: feeds, API behavior, missing data */}
                {f.feeds && f.feeds.length > 0 && (<>
                  <div style={sectionStyle("What feeds this", T.accent)}>What feeds this</div>
                  {f.feeds.map((a, j) => (
                    <div key={j} style={{ fontSize: 12, color: T.textSub, paddingLeft: 12, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: T.accent }}>•</span> {a}
                    </div>
                  ))}
                </>)}

                {f.withApi && (<>
                  <div style={sectionStyle("With API connected", T.green)}>With API connected</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, padding: "8px 12px", background: T.green + "08", borderRadius: RADIUS.sm }}>
                    {f.withApi}
                  </div>
                </>)}

                {f.withoutApi && (<>
                  <div style={sectionStyle("Without API (manual data)", T.orange)}>Without API (manual data)</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, padding: "8px 12px", background: T.orange + "08", borderRadius: RADIUS.sm }}>
                    {f.withoutApi}
                  </div>
                </>)}

                {f.missing && (<>
                  <div style={sectionStyle("When data is missing", T.red)}>When data is missing</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, padding: "8px 12px", background: T.red + "08", borderRadius: RADIUS.sm }}>
                    {f.missing}
                  </div>
                </>)}

                {/* Chart-specific: visibility */}
                {f.visibility && (<>
                  <div style={sectionStyle("When it appears", T.teal || "#06b6d4")}>When it appears</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, padding: "8px 12px", background: (T.teal || "#06b6d4") + "08", borderRadius: RADIUS.sm }}>
                    {f.visibility}
                  </div>
                </>)}

                {f.warn && (
                  <div style={{ marginTop: 12, fontSize: 12, color: T.orange, padding: "8px 12px", background: T.orange + "08", borderRadius: RADIUS.sm, border: `1px solid ${T.orange}25` }}>
                    ⚠ {f.warn}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
